import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  DialogTitle,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { LucidePrinter } from 'lucide-react';

import { generateCheckPdfAtom } from '../../_graphql/mutationGenerateCheckPdf';

import { useTranslations } from '@/finance/i18n/useTranslations';

interface PrintChecksDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPaymentIds: string[];
  onSuccess?: () => void;
}

export function PrintChecksDialog({
  isOpen,
  onOpenChange,
  selectedPaymentIds,
  onSuccess,
}: PrintChecksDialogProps) {
  const t = useTranslations();
  const toast = useToast();
  const generateCheckPdf = useSetAtom(generateCheckPdfAtom());

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const result = await generateCheckPdf({
        paymentIds: selectedPaymentIds,
      });

      const fileId = result?.financeGenerateCheckPdf?.fileId;
      if (fileId) {
        window.open(`/api/files/${fileId}`, '_blank', 'noopener,noreferrer');
      }

      toast.success(t.payments.printChecks.messages.success);
      onSuccess?.();
      handleClose();
    } catch (error) {
      toast.error(
        t.payments.printChecks.messages.error(
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleClose}
      className="@PrintChecksDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle>{t.payments.printChecks.title}</DialogTitle>
        </DialogHeader>
        <DialogMain>
          <div className="flex flex-col gap-2">
            <div className="text-sm text-neutral/60">
              {t.payments.printChecks.description({
                count: selectedPaymentIds.length,
              })}
            </div>
          </div>
        </DialogMain>
        <DialogFooter>
          <div className="flex justify-end gap-2">
            <Button variant="text" color="neutral" onPress={handleClose}>
              {t.payments.printChecks.buttons.cancel}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleSubmit}
              isPending={isSubmitting}
              icon={<LucidePrinter className="size-4" />}
            >
              {t.payments.printChecks.buttons.print}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
}
